<template>
    <group-details :groupId="props.groupId">
        <form-wrapper
            :sections="[
                { id: 'group_id', label: 'ID' },
                { id: 'name', label: 'Name' },
                { id: 'description', label: 'Description' },
                { id: 'attributes', label: 'Attributes' },
            ]"
            :hideNav="success || props.groupId"
        >

            <vx-alert v-if="success"
                        color="success"
                        :closeable="false"
                        shadow>
                <template v-slot:icon>
                    <font-awesome-icon :icon="faCircleCheck"></font-awesome-icon>
                </template>
                <div class="vx-flex vx-items-center vx-justify-between">
                    <span>
                        <strong>Existing Group</strong> updated successfully
                    </span>
                    <vx-button v-if="onCorporate()" :href="`/settings/teams/${teamId}#/groups`"
                                size="sm"
                                color="success">Back to Groups<font-awesome-icon
                        :icon="faSquareArrowUpLeft"></font-awesome-icon></vx-button>
                </div>
            </vx-alert>

            <vx-alert v-if="error"
                        color="danger"
                        :closeable="false"
                        shadow>{{ error }}</vx-alert>

            <group-form-external-id id="group_id"
                v-model="group.group_id"
                :locked="!!props.groupId || Boolean(queryParams.setup_group_id)"
                :disabled="!!props.groupId || Boolean(queryParams.setup_group_id)"
                :requiredLabel="!props.groupId"
                :error="errors.name || displayFirstError(validation.group_id.$errors)">
            </group-form-external-id>

            <group-form-name id="name"
                v-model="group.name"
                :error="errors.name || displayFirstError(validation.name.$errors)">
            </group-form-name>

            <group-form-description id="description"
                v-model="group.description"
                :error="errors.description || displayFirstError(validation.description.$errors)">
            </group-form-description>

            <group-form-phone-numbers v-if="props.groupId && !groupsUseCorporateNumbers"
                v-model="phoneNumbers">
            </group-form-phone-numbers>

            <group-form-custom-attributes id="attributes"
                v-model="customAttributes">
            </group-form-custom-attributes>

            <vx-sticky-footer>
                <vx-button :href="`/settings/teams/${teamId}#/groups`"
                    v-if="onCorporate()"
                    class="vx-w-20"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click="submit()"
                    :disabled="!onCorporate() && !['admin'].includes(roleOnCurrentGroup)"
                    data-test="submit-button"
                    class="vx-grow"
                    size="lg"
                    color="primary"
                    :loading="submitting">
                    {{
                        props.groupId
                            ? 'Update'
                            : 'Save'
                    }}
                </vx-button>
            </vx-sticky-footer>

        </form-wrapper>
    </group-details>
</template>

<script setup>
import {
    VxButton,
    VxAlert
} from "@voxie/frontend-components";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleCheck, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { ref, onMounted } from 'vue';
import { VxStickyFooter } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import useVuelidate from '@vuelidate/core';
import { required, maxLength, helpers } from '@vuelidate/validators';
import { alphanumericDashUnderscore, displayFirstError } from '~/utils/validation';
import GroupDetails from '~/components/settings/teams/groups/form/GroupDetails.vue';
import GroupFormExternalId from '~/components/settings/teams/groups/form/GroupFormExternalId.vue';
import GroupFormName from '~/components/settings/teams/groups/form/GroupFormName.vue';
import GroupFormDescription from './GroupFormDescription.vue';
import GroupFormCustomAttributes from './GroupFormCustomAttributes.vue';
import groupsClient from '~/services/groupsClient';
import GroupFormPhoneNumbers from './GroupFormPhoneNumbers.vue';
import phoneNumbersClient from '~/services/phoneNumbersClient';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useGroupDetails } from "./useGroupDetails";
import { useUrlSearchParams } from '@vueuse/core';
import { useCurrentGroup } from "~/composables/useCurrentGroup";
import FormWrapper from "~/components/general/FormWrapper.vue";

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;
const groupsUseCorporateNumbers = Spark.state.currentTeam.groups_use_corporate_numbers;

const props = defineProps({
    groupId: {
        type: String,
        required: false,
    },
});

const { group, mount, onMountedGroup } = useGroupDetails();
const { onCorporate, roleOnCurrentGroup } = useCurrentGroup();

const queryParams = useUrlSearchParams();

const error = ref();
const errors = ref({});
const success = ref(false);
const submitting = ref(false);
const loadingPhoneNumbers = ref(false);
const loadingCustomAttributes = ref(false);

const customAttributes = ref([]);
const originalCustomAttributes = ref([]);
const phoneNumbers = ref([]);

onMounted(() => {
    mount(props.groupId);
});

onMountedGroup(() => {
    if (!props.groupId && queryParams.setup_group_id) {
        group.value.group_id = queryParams.setup_group_id;
    }

    if (props.groupId) {
        getGroupCustomAttributes();

        if (!groupsUseCorporateNumbers) {
            getGroupPhoneNumbers();
        }
    }
})

const validation = useVuelidate({
    group_id: {
        required,
        maxLength: maxLength(40),
        format: alphanumericDashUnderscore,
    },
    name: {
        required,
        maxLength: maxLength(80),
        format: helpers.withMessage('You can use only alphanumeric letters and the following symbols: _-', helpers.regex(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/)),
    },
    description: { maxLength: maxLength(2000) },
}, group, { $autoDirty: true });

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validation.value.$validate();

    if (!isValid) {
        console.info(validation.value.$errors)
        toasted.error('Please check form data.');
        error.value = 'Please check form data.';
        success.value = false;
        return;
    }

    submitting.value = true;

    try {
        if (!props.groupId) {
            const response = await groupsClient.createGroup(teamId, group.value);

            await Promise.all(customAttributes.value.map((customAttribute) => {
                return groupsClient.createGroupCustomAttribute(teamId, response.data.group_id, customAttribute);
            }));

            window.location.href = `/settings/teams/${teamId}/groups/${response.data.group_id}/users`
        } else {
            await groupsClient.updateGroup(teamId, props.groupId, { name: group.value.name, description: group.value.description });
            await syncGroupCustomAttributes();

            originalCustomAttributes.value = customAttributes.value.map(attribute => ({...attribute}));

            success.value = true;
            error.value = null;
            queryParams.setup_group_id = undefined;
            validation.value.$reset();
        }
    } catch (e) {
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'Something went wrong.'
        } else {
            error.value = e.response?.data?.message || 'Something went wrong.';
        }
        toasted.error(error.value);

        // Remove success alert card if visible
        success.value = false;
    } finally {
        submitting.value = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
};

const getGroupCustomAttributes = async () => {
    try {
        loadingCustomAttributes.value = true;
        const response = await groupsClient.listGroupCustomAttributes(teamId, props.groupId);
        customAttributes.value = response.data.data.map(attribute => ({...attribute}));
        originalCustomAttributes.value = response.data.data.map(attribute => ({...attribute}));
    } catch (e) {
        error.value = e.response?.data?.message || 'Something went wrong.';
        toasted.error(error.value);
    } finally {
        loadingCustomAttributes.value = false;
    }
};

const getGroupPhoneNumbers = async () => {
    try {
        loadingPhoneNumbers.value = true;
        const response = await phoneNumbersClient.listPhoneNumbers(teamId, { group_id: props.groupId });
        phoneNumbers.value = response.data.data;
    } catch (e) {
        error.value = e.response?.data?.message || 'Something went wrong.';
        toasted.error(error.value);
    } finally {
        loadingPhoneNumbers.value = false;
    }
};

const syncGroupCustomAttributes = () => {
    return Promise.all([deleteCustomAttributes(), updateCustomAttributes(), createCustomAttributes()]);
};

const deleteCustomAttributes = () => {
    const attributesToDelete = originalCustomAttributes.value.filter(
        attribute => !customAttributes.value.map(a => a.key).includes(attribute.key)
    );

    return Promise.all(attributesToDelete.map((customAttribute) => {
        return groupsClient.deleteGroupCustomAttribute(teamId, props.groupId, customAttribute.key);
    }));
};

const updateCustomAttributes = () => {
    const attributesToUpdate = customAttributes.value.filter(
        attribute => {
            const value = originalCustomAttributes.value.find(a => a.key === attribute.key)?.value;
            return undefined !== value && value !== attribute.value;
        }
    );

    return Promise.all(attributesToUpdate.map((customAttribute) => {
        return groupsClient.updateGroupCustomAttribute(teamId, props.groupId, customAttribute.key, { value: customAttribute.value });
    }));
};

const createCustomAttributes = () => {
    const attributesToCreate = customAttributes.value.filter(
        attribute => !originalCustomAttributes.value.map(a => a.key).includes(attribute.key)
    );

    return Promise.all(attributesToCreate.map((customAttribute) => {
        return groupsClient.createGroupCustomAttribute(teamId, props.groupId, customAttribute);
    }));
};
</script>
